import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChatMemoryRequestDto } from 'src/api/FiretrolApiClient';
import { chatMemoryApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

export function useSetChatMemoryEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      chatMemoryApiClient.setEnabled({ enabled }),
    meta: { errorContext: 'set chat memory enabled' },
    onSettled: () => invalidateFor('chat.memory.write', qc),
  });
}

export function useCreateChatMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: ChatMemoryRequestDto) =>
      chatMemoryApiClient.create(request),
    meta: { suppressErrorToast: true, errorContext: 'create chat memory' },
    onSettled: () => invalidateFor('chat.memory.write', qc),
  });
}

export interface IUpdateChatMemoryArgs {
  id: number;
  request: ChatMemoryRequestDto;
}

export function useUpdateChatMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: IUpdateChatMemoryArgs) =>
      chatMemoryApiClient.update(id, request),
    meta: { suppressErrorToast: true, errorContext: 'update chat memory' },
    onSettled: () => invalidateFor('chat.memory.write', qc),
  });
}

export function useDeleteChatMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => chatMemoryApiClient.delete(id),
    meta: { errorContext: 'delete chat memory' },
    onSettled: () => invalidateFor('chat.memory.write', qc),
  });
}

export function useDeleteAllChatMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => chatMemoryApiClient.deleteAll(),
    meta: { errorContext: 'delete all chat memory' },
    onSettled: () => invalidateFor('chat.memory.write', qc),
  });
}

export function useAcceptChatMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => chatMemoryApiClient.accept(id),
    meta: { errorContext: 'accept chat memory' },
    onSettled: () => invalidateFor('chat.memory.write', qc),
  });
}

export function useDeclineChatMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => chatMemoryApiClient.decline(id),
    meta: { errorContext: 'decline chat memory' },
    onSettled: () => invalidateFor('chat.memory.write', qc),
  });
}

export interface IDeleteUserChatMemoryArgs {
  userId: number;
  id: number;
}

export function useDeleteUserChatMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, id }: IDeleteUserChatMemoryArgs) =>
      chatMemoryApiClient.deleteForUser(userId, id),
    meta: { errorContext: 'delete user chat memory' },
    onSettled: (_data, _err, { userId }) =>
      invalidateFor('chatAdmin.userMemory.delete', qc, { userId }),
  });
}
